import { ModelSetTransformer, ModelTransformer } from '../common/common.model';
import { Util } from '../common/common.util';

export interface ExtMi2DetailsPopupItem {
    userId: number;
    userName: string;
    previousScorecardId: number;
    previousScorecardPoints: number;
    previousStartDate: string;
    previousEndDate: string;
    previousScorecardClass: string;
    previousScorecardWidth: number;
    currentScorecardId: number;
    currentScorecardPoints: number;
    currentStartDate: string;
    currentEndDate: string;
    currentScorecardClass: string;
    currentScorecardWidth: number;
    movement: number;
    movementClass: string;
    overallPoints: number;
}

export interface Mi2DetailsPopupItemScorecard {
    id: number;
    points: number;
    startDate: Date;
    endDate: Date;
    class_: string;
}

export interface Mi2DetailsPopupItemMovement {
    points: number;
    class_: string;
}

export interface Mi2DetailsPopupItem {
    userId: number;
    username: string;
    previous: Mi2DetailsPopupItemScorecard;
    current: Mi2DetailsPopupItemScorecard;
    movement: Mi2DetailsPopupItemMovement;
    points: number;
}

export interface Mi2DetailsPopup {
    overall: Mi2DetailsPopupItem;
    items: Mi2DetailsPopupItem[];
}

class Mi2DetailsPopupItemScorecardModelTransformer
    implements
        ModelTransformer<Mi2DetailsPopupItemScorecard, ExtMi2DetailsPopupItem>
{
    constructor(private readonly _prefix: string) {}

    transform(model: ExtMi2DetailsPopupItem): Mi2DetailsPopupItemScorecard {
        return {
            id: model[`${this._prefix}ScorecardId`],
            points: model[`${this._prefix}ScorecardPoints`],
            startDate: Util.dateToDate(model[`${this._prefix}StartDate`]),
            endDate: Util.dateToDate(model[`${this._prefix}EndDate`]),
            class_: model[`${this._prefix}ScorecardClass`],
        };
    }
}

class Mi2DetailsPopupItemMovementModelTransformer
    implements
        ModelTransformer<Mi2DetailsPopupItemMovement, ExtMi2DetailsPopupItem>
{
    transform(model: ExtMi2DetailsPopupItem): Mi2DetailsPopupItemMovement {
        return {
            points: model.movement,
            class_: model.movementClass,
        };
    }
}

class Mi2DetailsPopupItemModelTransformer
    implements ModelTransformer<Mi2DetailsPopupItem, ExtMi2DetailsPopupItem>
{
    transform(model: ExtMi2DetailsPopupItem): Mi2DetailsPopupItem {
        const previous = new Mi2DetailsPopupItemScorecardModelTransformer(
            'previous',
        ).transform(model);
        const current = new Mi2DetailsPopupItemScorecardModelTransformer(
            'current',
        ).transform(model);
        const movement =
            new Mi2DetailsPopupItemMovementModelTransformer().transform(model);
        return {
            userId: model.userId,
            username: model.userName,
            previous,
            current,
            movement,
            points: model.overallPoints,
        };
    }
}

class Mi2DetailsPopupItemModelSetTransformer extends ModelSetTransformer<
    Mi2DetailsPopupItem,
    ExtMi2DetailsPopupItem
> {
    protected newTransformer(): ModelTransformer<
        Mi2DetailsPopupItem,
        ExtMi2DetailsPopupItem
    > {
        return new Mi2DetailsPopupItemModelTransformer();
    }
}

export class Mi2DetailsPopupModelTransformer
    implements ModelTransformer<Mi2DetailsPopup, ExtMi2DetailsPopupItem[]>
{
    transform(model: ExtMi2DetailsPopupItem[]): Mi2DetailsPopup {
        const overall_ = model.filter((value) => value.userId <= 0);
        let overall;
        if (overall_.length) {
            overall = new Mi2DetailsPopupItemModelTransformer().transform(
                overall_[0],
            );
        }

        const items_ = model.filter((value) => value.userId > 0);
        const items = new Mi2DetailsPopupItemModelSetTransformer().transform(
            items_,
        );

        return {
            overall,
            items,
        };
    }
}

export function validateMi2DetailsPopup(model: Mi2DetailsPopup): void {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const valid =
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        model != null &&
        model.overall != null &&
        model.items != null &&
        model.items.length !== 0;
    if (!valid) {
        throw new Error('Mi2DetailsPopup not valid');
    }
}
